import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { User, LogOut, Bell, Settings, ArrowRight, ChevronDown, Menu, X, Activity } from 'lucide-react';
import { useAuth } from '@/context/auth-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface UniversalHeaderProps {
  title?: string;
  showUserActions?: boolean;
  className?: string;
}

type NavLinkItem = {
  label: string;
  to: string;
  external?: boolean;
  badge?: string;
};

const UniversalHeader: React.FC<UniversalHeaderProps> = ({
  showUserActions = true,
  className = ''
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // State to determine if we have active notifications
  // TODO: Replace with real notification system - currently disabled
  const [hasNotifications, setHasNotifications] = React.useState(false);

  // Handle notification dismissal with localStorage persistence
  const handleDismissNotification = () => {
    localStorage.setItem('notificationDismissed', 'true');
    setHasNotifications(false);
  };

  const isAuthPage = location.pathname.includes('/auth') ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/forgot-password');

  // Navigation links for unauthenticated users
  const publicLinks: NavLinkItem[] = [
    { label: 'Academic Institutions', to: '/institution-signup' },
    { label: 'Other Providers', to: '/providers' },
    { label: 'About Us', to: '/about' },
    { label: 'Pricing', to: '/pricing' },
  ];

  // Navigation links for authenticated users
  const authLinks: NavLinkItem[] = [
    { label: 'Dashboard', to: '/app/dashboard' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'About Us', to: '/about' },
  ];

  const renderNavLink = (link: NavLinkItem) => {
    const isActive = !link.external && location.pathname.startsWith(link.to);
    const baseClasses = 'relative inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200';
    const activeClasses = 'bg-white/20 text-white border border-white/30';
    const inactiveClasses = 'text-gray-300 hover:text-white hover:bg-white/10';

    const content = (
      <>
        <span>{link.label}</span>
        {link.badge && (
          <span className="rounded-sm bg-white/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold text-white">
            {link.badge}
          </span>
        )}
      </>
    );

    if (link.external) {
      return (
        <a
          key={link.label}
          href={link.to}
          className={`${baseClasses} ${inactiveClasses}`}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        key={link.label}
        to={link.to}
        className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
      >
        {content}
      </Link>
    );
  };

  const showPublicNav = true;

  return (
    <>
      {/* Main Header */}
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${className} overflow-hidden shadow-md`}>
        {/* Base Dark Background (Left Side) */}

        <div className="absolute inset-0 bg-[#0F172A]" />

        {/* Sharp White Background (Right Side) - Shorter Division */}
        <div
          className="absolute inset-y-0 right-0 w-[40%] bg-white z-0"
          style={{ clipPath: 'polygon(25% 0, 100% 0, 100% 100%, 0% 100%)' }}
        />

        {/* Subtle texture overlay */}
        <div className="absolute inset-0 z-0 opacity-10 bg-[url('/assets/pattern.svg')] bg-repeat mix-blend-overlay" />

        <div className="container mx-auto px-4 py-4 relative z-20">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Logo */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                {/* Logo */}
                <img
                  src="/logo.png"
                  alt="CareVault Logo"
                  className="h-10 w-auto object-contain hover:opacity-90 transition-opacity"
                />

                {/* Brand text - hidden on mobile */}
                <div className="text-left hidden sm:block">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-white">
                      Care<span className="text-[#D64463]">Vault</span>
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Center: Navigation Links (for public pages) */}
            {showPublicNav && (
              <nav className="hidden md:flex items-center gap-1 mr-auto ml-8">
                {(user ? authLinks : publicLinks).map(renderNavLink)}
              </nav>
            )}

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {!user && !isAuthPage && (
                <>
                  <Button
                    variant="ghost"
                    asChild
                    className="hidden sm:flex text-slate-700 hover:text-[#D64463] hover:bg-[#D64463]/10 font-medium"
                  >
                    <Link to="/login">Sign In</Link>
                  </Button>
                  <Button
                    asChild
                    className="hidden md:flex bg-gradient-to-r from-[#D64463] to-[#9C27B0] text-white hover:opacity-90 shadow-lg shadow-[#D64463]/25 transition-all duration-200 rounded-xl text-xs sm:text-sm px-3 py-2 sm:px-4 sm:py-2 h-8 sm:h-10"
                  >
                    <Link to="/register" className="flex items-center gap-1">
                      <span className="whitespace-nowrap">Get Started</span>
                      <ArrowRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    </Link>
                  </Button>
                </>
              )}

              {!user && isAuthPage && (
                <div className="flex items-center gap-2">
                  {location.pathname === '/register' && (
                    <Button
                      variant="ghost"
                      asChild
                      className="text-slate-700 hover:text-[#D64463] hover:bg-[#D64463]/10 font-medium"
                    >
                      <Link to="/login">Sign In</Link>
                    </Button>
                  )}
                  {location.pathname.includes('/login') && (
                    <Button
                      asChild
                      variant="ghost"
                      className="text-slate-700 hover:text-[#D64463] hover:bg-[#D64463]/10 font-medium"
                    >
                      <Link to="/register">Create Account</Link>
                    </Button>
                  )}
                </div>
              )}

              {user && showUserActions && (
                <>
                  {/* Notifications */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-gray-800 dark:text-gray-300 hover:text-[#D64463] hover:bg-[#D64463]/10 transition-all duration-200"
                  >
                    <Bell className="h-5 w-5" />
                    {hasNotifications && (
                      <span className="absolute top-2 right-2 flex h-2 w-2 rounded-full bg-[#D64463] ring-2 ring-white dark:ring-[#1F303B]" />
                    )}
                  </Button>

                  {/* User Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center gap-3 px-3 py-2 text-gray-900 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-700 shadow-sm bg-white/50 backdrop-blur-sm"
                      >
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#D64463] to-[#9C27B0] flex items-center justify-center text-white border-2 border-white dark:border-[#2A3441] shadow-sm">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="hidden sm:block text-left mr-1">
                          <div className="text-sm font-bold leading-none text-slate-900">
                            {user.profile?.firstName || 'User'}
                          </div>
                          <div className="text-[10px] text-slate-600 font-bold uppercase tracking-wider mt-0.5">Personal</div>
                        </div>
                        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="w-56 bg-white dark:bg-[#2A3441] border-gray-200 dark:border-gray-700 shadow-xl rounded-xl p-2">
                      <div className="px-2 py-2 mb-2 border-b border-gray-100 dark:border-gray-700">
                        <div className="font-semibold text-gray-900 dark:text-white">{user.profile?.firstName} {user.profile?.lastName}</div>
                        <div className="text-xs text-gray-500 truncate">{user.email}</div>
                      </div>

                      <div className="space-y-1">
                        <DropdownMenuItem asChild className="flex items-center gap-2 cursor-pointer rounded-lg focus:bg-[#D64463]/10 focus:text-[#D64463]">
                          <Link to="/app/dashboard">
                            <Activity className="h-4 w-4" />
                            <span>Dashboard</span>
                          </Link>
                        </DropdownMenuItem>

                        <DropdownMenuItem asChild className="flex items-center gap-2 cursor-pointer rounded-lg focus:bg-[#D64463]/10 focus:text-[#D64463]">
                          <Link to="/app/settings">
                            <Settings className="h-4 w-4" />
                            <span>Settings</span>
                          </Link>
                        </DropdownMenuItem>
                      </div>

                      <DropdownMenuSeparator className="my-2 bg-gray-100 dark:bg-gray-700" />

                      <DropdownMenuItem
                        onClick={logout}
                        className="flex items-center gap-2 text-red-500 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/10 cursor-pointer rounded-lg"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Sign Out</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}

              {/* Mobile Menu Button */}
              {showPublicNav && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden text-[#9C27B0] hover:text-[#D64463] hover:bg-[#9C27B0]/10 transition-all duration-300 hover:scale-110 active:scale-95"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6 transition-transform duration-300 rotate-90" />
                  ) : (
                    <Menu className="h-6 w-6 transition-transform duration-300 hover:rotate-180" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {showPublicNav && mobileMenuOpen && (
        <div className="md:hidden bg-[#111827] border-b border-white/10 px-4 py-3 fixed top-[72px] left-0 right-0 z-40 shadow-xl">
          <nav className="flex flex-col gap-2">
            {(user ? authLinks : publicLinks).map(renderNavLink)}
            {!user && (
              <div className="pt-3 mt-2 border-t border-white/10 space-y-2">
                <Button
                  asChild
                  className="w-full bg-gradient-to-r from-[#D64463] to-[#9C27B0] text-white hover:opacity-90 shadow-lg shadow-[#D64463]/25 transition-all duration-200 rounded-xl h-10"
                >
                  <Link to="/register" className="flex items-center justify-center gap-2">
                    <span>Get Started</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="ghost"
                  className="w-full text-gray-300 hover:text-white hover:bg-white/10"
                >
                  <Link to="/login">Sign In</Link>
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}

      {/* Notification Banner - Only shows when there are notifications */}
      {user && hasNotifications && (
        <div className="bg-[#111827] border-b border-white/10 px-4 py-3">
          <div className="container mx-auto flex items-center gap-3">
            <Bell className="h-4 w-4 text-rose-400 flex-shrink-0" />
            <span className="text-sm text-gray-300">
              You have 3 new notifications that require your attention
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-xs text-gray-400 hover:text-white hover:bg-white/10"
            >
              View all
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-gray-400 hover:text-white hover:bg-white/10"
              onClick={handleDismissNotification}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default UniversalHeader;
